// @purpose STATE
// Wedge mini-quiz client (M5.1/M5.2). Loaded only on /q/<slug>/. Reuses the full test's Alpine
// quad component and motion preference, and the htmx + Alpine instances main.js already started.
//  - registers calmQuad, then initialises #wedge-region (it ships x-ignore'd);
//  - adds latency / motion mode / render tier to every answer POST (22b §5);
//  - keyboard: 1-9 pick, Enter next; after a round swap the card scrolls under the bar and focus
//    moves to its heading (audit v3 F2); the round's "Motion · full / calm / off" toggle (F6);
//  - analytics: wedge_next_suggested when a core sample with a suggestion renders (schema-checked
//    track()); wedge_started / wedge_completed are server-side events, never sent from here.
import { track } from "../track.js";
import { calmQuad } from "../test/components.js";
import { focusScreen } from "../test/focus.js";
import { applyMode, currentMode, cycleMode } from "../test/motion.js";

const RENDER_TIER = "3";
let registered = false;

const region = () => document.getElementById("wedge-region");

function initRegion() {
  const el = region();
  const Alpine = window.Alpine;
  if (!el || !Alpine || !el.hasAttribute("x-ignore")) return;
  if (!registered) {
    Alpine.data("calmQuad", calmQuad);
    registered = true;
  }
  el.removeAttribute("x-ignore");
  delete el._x_ignore;
  Alpine.initTree(el);
}

function suggested(root) {
  const box = root && root.querySelector ? root.querySelector('[data-track="wedge_next_suggested"]') : null;
  if (!box || box.dataset.tracked || !box.dataset.to) return;
  box.dataset.tracked = "1";
  track("wedge_next_suggested", {
    from_wedge: box.dataset.from,
    to_wedge: box.dataset.to,
    measured_count: Number(box.dataset.measured || 0),
  });
}

document.addEventListener("htmx:configRequest", (e) => {
  const form = e.detail.elt;
  if (!form || !form.matches || !form.matches("#wedge-region [data-answer-form]")) return;
  const shown = Number(form.dataset.shownAt || Date.now());
  Object.assign(e.detail.parameters, {
    latency_ms: String(Math.max(0, Date.now() - shown)),
    motion_mode: currentMode(),
    render_tier: RENDER_TIER,
  });
});

document.addEventListener("htmx:afterSettle", (e) => {
  const el = region();
  if (!el || !el.contains(e.detail.target)) return;
  if (e.detail.target !== el) {
    // the core sample replacing its skeleton: move focus to its heading, leave the scroll alone
    const heading = e.detail.target.querySelector && e.detail.target.querySelector("h1, .t-title, h2");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    }
    return;
  }
  applyMode();
  focusScreen(el, currentMode() === "full");
});

document.addEventListener("click", (e) => {
  const toggle = e.target.closest && e.target.closest("#wedge-region [data-motion-toggle]");
  if (toggle) cycleMode();
});

document.addEventListener("htmx:load", (e) => {
  initRegion();
  suggested(e.detail.elt && e.detail.elt.parentNode);
});

document.addEventListener("keydown", (e) => {
  if (!document.getElementById("wedge-root") || e.defaultPrevented) return;
  if (e.altKey || e.ctrlKey || e.metaKey) return;
  const form = document.querySelector("#wedge-region form[data-answer-form]");
  if (!form) return;
  if (/^[1-9]$/.test(e.key)) {
    const btn = form.querySelector(`[data-key="${e.key}"]`);
    if (btn) {
      e.preventDefault();
      btn.click();
      btn.focus();
    }
  } else if (e.key === "Enter") {
    const next = form.querySelector("[data-next]");
    if (next && !next.disabled) {
      e.preventDefault();
      form.requestSubmit(next);
    }
  }
});

applyMode();
initRegion();
suggested(document);
